/* THE CONVERSATION ON A DRAFTING SCREEN, KEPT — Sites (supabase/0048,
 * 2026-10-28) and the other five panels that use the Drafter (0049, 2026-10-30).
 *
 * Until now the drafting chat lived in the screen: leaving the page, moving to
 * another section or a refresh threw it away — and with it any draft still
 * waiting in the lock card. Her BBA landing page lost its last two drafts that
 * way on 2026-10-15. This is `utils/socialChat.js` for a page, with two
 * differences that come from how Sites drafts:
 *
 *   · ONE ROW PER PAGE holding SEVERAL chats — "page" for the whole page and
 *     one per section, keyed by the section's id — because each section has
 *     its own conversation and the pager moves between them.
 *   · THE WAITING DRAFT IS KEPT TOO (`pending`). The lock card is the one
 *     place Brill's words live before she locks them (rule 4), so it is the
 *     part that hurt most to lose. It comes back as the same card, still hers
 *     to lock or dismiss — never written into her fields.
 *
 * A chat is { turns: [{ role, content }], pending } — turns exactly as the
 * Drafter holds them, so nothing is translated on the way in or out.
 *
 * COST: saving is a few kilobytes in her own Supabase. What is SENT to Brill
 * is `sendWindow` — capped, so a chat kept for weeks cannot grow every
 * message's price for ever.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useSupabase } from "../supabaseClient";

export const PAGE_KEY = "page";
export const KEEP_TURNS = 40;   // stored per chat, newest kept
export const SEND_TURNS = 30;   // sent to Brill per message, newest kept

const hasHerTurn = (turns) => (turns || []).some((t) => t?.role === "user");

/** Bounded for storage, and null when there is nothing worth keeping: only
 *  Brill's opening line, and no draft waiting. */
export function cleanChat(entry) {
  const turns = (entry?.turns || [])
    .filter((t) => t && (t.role === "user" || t.role === "assistant"))
    .map((t) => ({ role: t.role, content: String(t.content || "") }))
    .slice(-KEEP_TURNS);
  const pending = entry?.pending ?? null;
  if (!hasHerTurn(turns) && !pending) return null;
  return { turns, pending };
}

/** The page's chats with one changed — and without any chat for a section the
 *  page no longer has, so deleting a section takes its chat with it.
 *  A chat with nothing worth keeping NEVER replaces a kept one: the screen
 *  opens on Brill's opener alone for a moment before the kept chat comes back,
 *  and that moment must not be written over what she said last week. */
export function withChat(chats = {}, key, entry, liveKeys = null) {
  const next = { ...(chats || {}) };
  const clean = cleanChat(entry);
  if (clean) next[key] = clean;
  if (liveKeys) {
    const live = new Set([PAGE_KEY, ...liveKeys]);
    for (const k of Object.keys(next)) if (!live.has(k)) delete next[k];
  }
  return next;
}

/** What goes to Brill: the newest SEND_TURNS, which is everything for any
 *  conversation held in one sitting. */
export function sendWindow(turns = []) {
  return (turns || []).slice(-SEND_TURNS);
}

/* ── Storage ─────────────────────────────────────────────────────────────── */

/** `useKeptChats(table, where, migration, liveKeys)` →
 *  { chats, loaded, problem, save(key, entry) }. `where` names the one row, or
 *  is null with no record open.
 *  Loads once per record. A failed save never blocks the chat — it carries on in
 *  the screen — but it is SAID, because "kept" and "not kept" look identical
 *  until she comes back and the conversation is gone. */
function useKeptChats(table, where, migration, liveKeys = null) {
  const rowKey = where ? JSON.stringify(where) : "";
  const whereRef = useRef(where);
  whereRef.current = where;
  /* A ref, as socialChat holds it: useSupabase hands back a new function on
     every render, and an effect keyed on it would reload on every keystroke. */
  const getSb = useSupabase();
  const sbRef = useRef(getSb);
  sbRef.current = getSb;
  const [chats, setChats] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [problem, setProblem] = useState("");
  const latest = useRef({});
  const keysRef = useRef(liveKeys);
  keysRef.current = liveKeys;

  useEffect(() => {
    let gone = false;
    latest.current = {};
    setChats({}); setLoaded(false); setProblem("");
    if (!rowKey) { setLoaded(true); return undefined; }
    (async () => {
      try {
        const sb = await sbRef.current();
        let read = sb.from(table).select("chats");
        for (const [col, val] of Object.entries(whereRef.current)) read = read.eq(col, val);
        const { data, error } = await read.maybeSingle();
        if (gone) return;
        if (error) setProblem(missingTable(table, error) ? notRun(migration) : "This conversation couldn't be loaded.");
        const c = data?.chats && typeof data.chats === "object" ? data.chats : {};
        latest.current = c;
        setChats(c);
      } catch {
        if (!gone) setProblem("This conversation couldn't be loaded.");
      } finally {
        if (!gone) setLoaded(true);
      }
    })();
    return () => { gone = true; };
  }, [table, rowKey, migration]);

  const save = useCallback(async (key, entry) => {
    const row = whereRef.current;
    if (!row || !key) return;
    const next = withChat(latest.current, key, entry, keysRef.current);
    if (JSON.stringify(next) === JSON.stringify(latest.current)) return;
    latest.current = next;
    try {
      const sb = await sbRef.current();
      const { error } = await sb.from(table)
        .upsert({ ...row, chats: next, updated_at: new Date().toISOString() }, { onConflict: Object.keys(row).join(",") });
      setProblem(error ? (missingTable(table, error) ? notRun(migration) : NOT_SAVED) : "");
    } catch {
      setProblem(NOT_SAVED);
    }
  }, [table, rowKey, migration]);

  return { chats, loaded, problem, save };
}

const notRun = (migration) => `This chat isn't being kept yet — the database change (${migration}) hasn't been run.`;
const NOT_SAVED = "This chat didn't save — it will be gone when you leave this screen.";
const missingTable = (table, error) => new RegExp(`${table}|does not exist|schema cache`, "i").test(error?.message || "");

/** A Sites page's chats (supabase/0048). */
export function usePageChat(pageId, liveKeys = null) {
  return useKeptChats("website_page_chats", pageId ? { page_id: pageId } : null, "0048", liveKeys);
}

/** What the shared Drafter takes as `keep`, from a store above: this block's
 *  chat under its id, the whole record's under `recordKey`. */
export function useDrafterKeep(kept, blockKey, recordKey = PAGE_KEY) {
  const keepSave = kept.save;
  const save = useCallback(
    (scope, entry) => keepSave(scope === "record" ? recordKey : blockKey, entry),
    [keepSave, blockKey, recordKey],
  );
  return {
    loaded: kept.loaded,
    block: blockKey ? kept.chats[blockKey] || null : null,
    record: kept.chats[recordKey] || null,
    save,
    problem: kept.problem,
    window: sendWindow,
  };
}

/** The `keep` for Courses, Outreach, Scripts, Newsletter and Presentations
 *  (supabase/0049): one `record_chats` row per record. CALL IT ABOVE THE PANEL'S
 *  `if (!x) return null` — a hook below a return is what took down Sites. */
export function useRecordKeep({ tool, recordId, blockKey, recordKey = PAGE_KEY, liveKeys = null }) {
  const kept = useKeptChats("record_chats", recordId ? { tool, record_id: recordId } : null, "0049", liveKeys);
  return useDrafterKeep(kept, blockKey, recordKey);
}
